/* Invoices index — every MerchantInvoice the merchant has issued,
 * whether spawned by a recurring template or created one-off.
 *
 * Route: /billing/invoices  (inside the auth shell, behind the same
 * billing.recurring feature gate as templates).
 *
 * Click an invoice → opens the detail drawer (MerchantInvoiceDrawer)
 * with mark-paid / void / resend actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCcw, FileText, ExternalLink, Copy
} from 'lucide-react';
import axios from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import { useCurrency } from '../../contexts/CurrencyContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import MerchantInvoiceDrawer from './MerchantInvoiceDrawer';
import InvoiceAnalytics from './InvoiceAnalytics';
import './MerchantInvoices.css';

/* ─── Module-scope helpers + subcomponents ──────────────────── */

const STATUS_PILLS = {
  DRAFT:   { label: 'Draft',   kind: 'neutral' },
  SENT:    { label: 'Sent',    kind: 'accrued' },
  PARTIAL: { label: 'Partial', kind: 'info'    },
  PAID:    { label: 'Paid',    kind: 'paid'    },
  OVERDUE: { label: 'Overdue', kind: 'overdue' },
  VOID:    { label: 'Void',    kind: 'neutral' },
};

const FILTERS = [
  { id: 'all',     label: 'All'     },
  { id: 'DRAFT',   label: 'Draft'   },
  { id: 'SENT',    label: 'Sent'    },
  { id: 'OVERDUE', label: 'Overdue' },
  { id: 'PAID',    label: 'Paid'    },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/* SENT past dueDate is logically OVERDUE — mirror the same derivation
   used in the POSv2 fee panel. Backend doesn't flip the status until
   the daily cron runs, so we surface it client-side too. */
function effectiveStatus(inv) {
  if (inv.status === 'SENT' && inv.dueDate && new Date(inv.dueDate) < new Date()) return 'OVERDUE';
  return inv.status;
}

function StatusPill({ status }) {
  const cfg = STATUS_PILLS[status] || { label: status, kind: 'neutral' };
  return <span className={`minv-pill minv-pill--${cfg.kind}`}>{cfg.label}</span>;
}

function FilterStrip({ value, onChange }) {
  return (
    <div className="minv-filter-strip" role="tablist" aria-label="Status filter">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          type="button"
          role="tab"
          aria-selected={value === f.id}
          className={`minv-filter-chip${value === f.id ? ' is-active' : ''}`}
          onClick={() => onChange(f.id)}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* Always use the backend-persisted invoice.paymentLink (built using
   INVOICE_PAY_DOMAIN — pay.userevstar.com / pay.staging.userevstar.com).
   window.location.origin is wrong here because the merchant is on
   dashboard.* / app.*, not pay.*; copying that URL to share with a
   customer would send them to the merchant subdomain. */
function CopyLinkButton({ invoice, toast }) {
  const [copied, setCopied] = useState(false);
  const handle = (e) => {
    e.stopPropagation();
    const url = invoice.paymentLink || `${window.location.origin}/invoice/${invoice.paymentToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success('Payment link copied');
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error('Copy failed'));
  };
  return (
    <button type="button" className="minv-icon-btn" title="Copy payment link" onClick={handle}>
      {copied ? <ExternalLink size={14} /> : <Copy size={14} />}
    </button>
  );
}

function InvoiceRow({ invoice, formatMoney, onOpen, toast }) {
  const status = effectiveStatus(invoice);
  return (
    <tr className="minv-row" onClick={() => onOpen(invoice)}>
      <td className="minv-cell minv-cell--main">
        <div className="minv-num">{invoice.invoiceNumber}</div>
        <div className="minv-customer">
          {invoice.customerName} · <span className="minv-muted">{invoice.customerEmail}</span>
        </div>
      </td>
      <td className="minv-cell minv-cell--money" data-label="Total">{formatMoney(invoice.total)}</td>
      <td className="minv-cell" data-label="Due">{fmtDate(invoice.dueDate)}</td>
      <td className="minv-cell" data-label="Status"><StatusPill status={status} /></td>
      <td className="minv-cell minv-cell--actions">
        {invoice.paymentToken && <CopyLinkButton invoice={invoice} toast={toast} />}
      </td>
    </tr>
  );
}

function EmptyState() {
  return (
    <div className="minv-empty">
      <FileText size={28} />
      <h3>No invoices yet</h3>
      <p>Generated invoices show up here. Create a recurring template, or send a one-off invoice from a customer record.</p>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────── */

export default function MerchantInvoices() {
  const { isAuthenticated } = useSecureAuth();
  const toast = useToast();
  const { formatInDisplayCurrency } = useCurrency();
  const formatMoney = useCallback((v) => formatInDisplayCurrency(v ?? 0), [formatInDisplayCurrency]);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [openInvoice, setOpenInvoice] = useState(null);
  const [stats, setStats]       = useState(null);
  // Page-level tab. Reads via ?tab= so the URL is shareable + the
  // refresh button keeps the active tab.
  const [activeTab, setActiveTab] = useState('invoices');

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get('/merchant/invoices/stats');
      setStats(data?.stats || null);
    } catch (err) {
      // Non-fatal — the list still works. Don't toast: the page
      // still renders the table below without the strip.
      console.warn('[MerchantInvoices] stats load failed:', err?.message);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (search.trim())    params.append('q', search.trim());
      const { data } = await axios.get(`/merchant/invoices?${params.toString()}`);
      setInvoices(data?.invoices || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [filter, search, toast]);

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated, load]);
  // Stats are filter-independent (always reflect the merchant's
  // full A/R position) — fetched once on mount, refreshed after
  // any action that could change them.
  useEffect(() => { if (isAuthenticated) loadStats(); }, [isAuthenticated, loadStats]);

  const handleDrawerSaved = useCallback(() => {
    setOpenInvoice(null);
    load();
    loadStats();
  }, [load, loadStats]);

  return (
    <Shell>
      <FeatureGate
        featureId="billing.recurring"
        upgradeMessage="Invoices are part of the Recurring Invoices feature. Upgrade to PRO or PREMIUM to send and track customer invoices."
      >
        <div className="minv-page">
          <header className="minv-header">
            <div className="minv-header-text">
              <h1>Invoices</h1>
              <p>Every invoice you've issued — from recurring templates and one-off sends. Click any row to view, mark paid, or resend.</p>
            </div>
            <div className="minv-header-actions">
              <button type="button" className="minv-btn minv-btn--ghost" onClick={() => { load(); loadStats(); }} disabled={loading} title="Refresh">
                <RefreshCcw size={14} />
              </button>
            </div>
          </header>

          {/* Page-level pill/toggle tab (per CLAUDE.md tab-switches
              rule — no underline tabs). */}
          <div className="minv-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'invoices'}
              className={`minv-tab${activeTab === 'invoices' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('invoices')}
            >
              Invoices
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'analytics'}
              className={`minv-tab${activeTab === 'analytics' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
          </div>

          {activeTab === 'invoices' ? (
            <>
              <div className="minv-controls">
                <FilterStrip value={filter} onChange={setFilter} />
                <label className="minv-search">
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Search invoice number, customer name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
              </div>

              {loading ? (
                <div className="minv-loading">Loading…</div>
              ) : invoices.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="minv-table-wrap">
                  <table className="minv-table">
                    <thead>
                      <tr>
                        <th>Invoice / Customer</th>
                        <th className="minv-th--money">Total</th>
                        <th>Due</th>
                        <th>Status</th>
                        <th className="minv-th--actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <InvoiceRow
                          key={inv.id}
                          invoice={inv}
                          formatMoney={formatMoney}
                          onOpen={setOpenInvoice}
                          toast={toast}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <InvoiceAnalytics stats={stats} loading={loading && !stats} formatMoney={formatMoney} />
          )}

          {openInvoice && (
            <MerchantInvoiceDrawer
              invoiceId={openInvoice.id}
              onClose={() => setOpenInvoice(null)}
              onSaved={handleDrawerSaved}
            />
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
}
